import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Button, Input, Textarea, Typography } from "@material-tailwind/react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import useAuth from "../hooks/useAuth";
import { ASSET_URI, PROXY } from "../lib/utils";
import { getLanguageDirection } from "../i18n/config";

const emptyForm = {
  titlePs: "",
  titlePr: "",
  titleEn: "",
  descPs: "",
  descPr: "",
  descEn: "",
  sloganPs: "",
  sloganPr: "",
  sloganEn: "",
  curriculumPs: "",
  curriculumPr: "",
  curriculumEn: "",
  duration: "",
  videoLang: "pashto",
  categoryId: "",
  subCategoryId: "",
};

const TeacherCourseForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { accessToken, user } = useAuth();
  const { t, i18n } = useTranslation();
  const isEditMode = Boolean(id);
  const [form, setForm] = useState(emptyForm);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [imageProgress, setImageProgress] = useState(0);
  const [courseImage, setCourseImage] = useState("");
  const [courseSlug, setCourseSlug] = useState("");
  const [isLocked, setIsLocked] = useState(false);
  const [canDelete, setCanDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletionReason, setDeletionReason] = useState("");

  useEffect(() => {
    let isMounted = true;

    const load = async () => {
      try {
        const requests = [
          fetch(`${PROXY}/categories`).then((res) => res.json()),
        ];

        if (isEditMode) {
          requests.push(
            fetch(`${PROXY}/teacher/courses/${id}`, {
              headers: {
                Authorization: `Bearer ${accessToken}`,
              },
            }).then(async (res) => {
              const payload = await res.json().catch(() => null);
              if (!res.ok) {
                throw new Error(payload?.message || t("teacherForm.toast_load_failed", "Failed to load course."));
              }
              return payload;
            }),
          );
        }

        const [categoriesPayload, coursePayload] = await Promise.all(requests);
        if (!isMounted) return;

        setCategories(Array.isArray(categoriesPayload) ? categoriesPayload : []);

        if (coursePayload) {
          const selectedSubCategoryId =
            coursePayload?.category?.subCategories?.[0]?._id || coursePayload?.subCategoryId || "";
          const categoryId =
            coursePayload?.category?._id ||
            categoriesPayload.find((category) =>
              Array.isArray(category?.subCategories)
                ? category.subCategories.some((sub) => sub._id === selectedSubCategoryId)
                : false,
            )?._id ||
            "";

          setForm({
            titlePs: coursePayload?.title?.ps || "",
            titlePr: coursePayload?.title?.pr || "",
            titleEn: coursePayload?.title?.en || "",
            descPs: coursePayload?.desc?.ps || "",
            descPr: coursePayload?.desc?.pr || "",
            descEn: coursePayload?.desc?.en || "",
            sloganPs: coursePayload?.slogan?.ps || "",
            sloganPr: coursePayload?.slogan?.pr || "",
            sloganEn: coursePayload?.slogan?.en || "",
            curriculumPs: coursePayload?.curriculum?.ps || "",
            curriculumPr: coursePayload?.curriculum?.pr || "",
            curriculumEn: coursePayload?.curriculum?.en || "",
            duration: coursePayload?.duration || "",
            videoLang: coursePayload?.videoLang || "pashto",
            categoryId,
            subCategoryId: selectedSubCategoryId,
          });
          setCourseImage(coursePayload?.image || "");
          setCourseSlug(coursePayload?.slug || "");
          setIsLocked(Boolean(coursePayload?.isLocked));
          const currentUserId = String(user?._id || user?.id || "");
          const instructorId = String(coursePayload?.instructor?._id || coursePayload?.instructor || "");
          const creatorId = String(coursePayload?.creatorId?._id || coursePayload?.creatorId || "");
          setCanDelete(
            user?.role === "teacher" && Boolean(currentUserId) &&
            (currentUserId === instructorId || currentUserId === creatorId),
          );
        }
      } catch (error) {
        if (isMounted) setLoadError(true);
        toast.error(error.message || t("teacherForm.toast_load_failed", "Failed to load course."));
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    load();

    return () => {
      isMounted = false;
    };
  }, [accessToken, id, isEditMode, user, t]);

  const subCategories = useMemo(() => {
    return categories.find((category) => category._id === form.categoryId)?.subCategories || [];
  }, [categories, form.categoryId]);

  const updateField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const validate = () => {
    if (!form.titlePs.trim() || !form.titlePr.trim() || !form.titleEn.trim()) {
      return t("teacherForm.validation_titles_required", "Course titles in all languages are required.");
    }
    if (!form.categoryId || !form.subCategoryId) {
      return t("teacherForm.validation_category_required", "Faculty and department are required.");
    }
    return "";
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const validationError = validate();
    if (validationError) {
      toast.error(validationError);
      return;
    }

    try {
      setSaving(true);
      const payload = {
        title: {
          ps: form.titlePs.trim(),
          pr: form.titlePr.trim(),
          en: form.titleEn.trim(),
        },
        desc: {
          ps: form.descPs.trim(),
          pr: form.descPr.trim(),
          en: form.descEn.trim(),
        },
        slogan: {
          ps: form.sloganPs.trim(),
          pr: form.sloganPr.trim(),
          en: form.sloganEn.trim(),
        },
        curriculum: {
          ps: form.curriculumPs.trim(),
          pr: form.curriculumPr.trim(),
          en: form.curriculumEn.trim(),
        },
        duration: form.duration.trim(),
        videoLang: form.videoLang,
        ...(isEditMode
          ? { subCategory: form.subCategoryId }
          : { subCategoryId: form.subCategoryId }),
      };

      const response = await fetch(
        isEditMode ? `${PROXY}/teacher/courses/${id}` : `${PROXY}/teacher/courses`,
        {
          method: isEditMode ? "PATCH" : "POST",
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        },
      );

      const result = await response.json().catch(() => null);
      if (!response.ok) {
        throw new Error(result?.message || t("teacherForm.toast_save_failed", "Failed to save course."));
      }

      toast.success(
        isEditMode
          ? t("teacherForm.toast_update_success", "Course updated successfully.")
          : t("teacherForm.toast_create_success", "Course created successfully."),
      );
      navigate(
        isEditMode
          ? `/teacher/courses/${id}/content`
          : `/teacher/courses/${result?._id}/content`,
      );
    } catch (error) {
      toast.error(error.message || t("teacherForm.toast_save_failed", "Failed to save course."));
    } finally {
      setSaving(false);
    }
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file || !id) {
      return;
    }

    const formData = new FormData();
    formData.append("image", file);

    try {
      setUploadingImage(true);
      setImageProgress(0);
      const response = await axios.post(
        `${PROXY}/teacher/courses/${id}/img/upload?img=${courseImage || ""}`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
          onUploadProgress: (progressEvent) => {
            const percent = Math.round((progressEvent.loaded * 100) / (progressEvent.total || 1));
            setImageProgress(percent);
          },
        },
      );

      setCourseImage(response?.data?.course?.image || courseImage);
      toast.success(t("teacherForm.toast_thumbnail_success", "Thumbnail updated successfully."));
    } catch (error) {
      toast.error(error.response?.data?.error || t("teacherForm.toast_thumbnail_failed", "Failed to upload thumbnail."));
    } finally {
      setUploadingImage(false);
    }
  };

  const handleDelete = async () => {
    if (!id || isLocked || !canDelete) return;
    const reason = deletionReason.trim();
    if (!reason) {
      toast.error(t("teacherForm.delete_reason_required", "Please provide a deletion reason."));
      return;
    }

    try {
      setDeleting(true);
      const response = await fetch(`${PROXY}/teacher/courses/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${accessToken}`, "Content-Type": "application/json" },
        body: JSON.stringify({ deletionReason: reason }),
      });
      const payload = await response.json().catch(() => null);
      if (!response.ok) throw new Error(payload?.message || t("teacherForm.toast_delete_failed", "Failed to remove course."));
      setDeleteDialogOpen(false);
      toast.success(t("teacherForm.toast_delete_success", "Course deleted successfully."));
      navigate("/teacher/courses");
    } catch (error) {
      toast.error(error.message || t("teacherForm.toast_delete_failed", "Failed to remove course."));
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return <div className="mt-10 text-center text-slate-500">{t("teacherForm.loading", "Loading course form...")}</div>;
  }

  if (loadError) {
    return <section dir={getLanguageDirection(i18n.language)} className="mx-auto mt-8 max-w-3xl rounded-2xl border border-red-200 bg-red-50 p-8 text-center text-red-800">
      <Typography variant="h4">{t("teacherContent.cannot_manage_title", "You cannot manage this course")}</Typography>
      <Typography className="mt-3">{t("teacherContent.cannot_manage_description", "The course was not found or you do not have permission to manage it.")}</Typography>
      <Link to="/teacher/courses" className="mt-5 inline-block"><Button variant="outlined" color="red">{t("teacherForm.back_to_studio", "Back to Teacher Studio")}</Button></Link>
    </section>;
  }

  return (
    <section
      dir={getLanguageDirection(i18n.language)}
      className="mx-auto mt-8 max-w-6xl px-4 pb-12 sm:px-6 lg:px-8"
    >
      <div className="flex flex-col gap-3 rounded-3xl border border-slate-200 bg-white p-6 shadow-sm lg:flex-row lg:items-center lg:justify-between">
        <div>
          <Typography variant="h4" className="text-slate-900">
            {isEditMode
              ? t("teacherForm.edit_title", "Edit Course")
              : t("teacherForm.create_title", "Create Course")}
          </Typography>
          <Typography className="mt-2 text-slate-600">
            {t(
              "teacherForm.subtitle",
              "Manage your own course metadata here, then move into content uploads and chapter work.",
            )}
          </Typography>
        </div>
        <Link to="/teacher/courses">
          <Button variant="outlined" color="blue-gray">
            {t("teacherForm.back_to_studio", "Back to Teacher Studio")}
          </Button>
        </Link>
      </div>

      {isEditMode && isLocked ? (
        <div className="mt-6 rounded-3xl border border-amber-200 bg-amber-50 p-6 text-center text-amber-900">
          <Typography variant="h5">{t("teacherForm.locked_title", "Course locked")}</Typography>
          <Typography className="mt-2">{t("teacherForm.locked_message", "This course is read-only while it is locked by an administrator.")}</Typography>
        </div>
      ) : null}

      <form onSubmit={handleSubmit} className="mt-6 space-y-6">
        <div className="grid grid-cols-1 gap-6 xl:grid-cols-[1.35fr_0.85fr]">
          <div className="space-y-6">
            <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
              <Typography variant="h6" className="text-slate-900">
                {t("teacherForm.course_titles", "Course titles")}
              </Typography>
              <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-3">
                <Input label={t("teacherForm.pashto_title", "Pashto title")} value={form.titlePs} onChange={(e) => updateField("titlePs", e.target.value)} />
                <Input label={t("teacherForm.dari_title", "Dari title")} value={form.titlePr} onChange={(e) => updateField("titlePr", e.target.value)} />
                <Input label={t("teacherForm.english_title", "English title")} value={form.titleEn} onChange={(e) => updateField("titleEn", e.target.value)} />
              </div>
            </div>

            <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
              <Typography variant="h6" className="text-slate-900">
                {t("teacherForm.descriptions", "Descriptions")}
              </Typography>
              <div className="mt-4 grid grid-cols-1 gap-4">
                <Textarea label={t("teacherForm.pashto_description", "Pashto description")} value={form.descPs} onChange={(e) => updateField("descPs", e.target.value)} />
                <Textarea label={t("teacherForm.dari_description", "Dari description")} value={form.descPr} onChange={(e) => updateField("descPr", e.target.value)} />
                <Textarea label={t("teacherForm.english_description", "English description")} value={form.descEn} onChange={(e) => updateField("descEn", e.target.value)} />
              </div>
            </div>

            <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
              <Typography variant="h6" className="text-slate-900">
                {t("teacherForm.slogan_curriculum", "Slogan and curriculum")}
              </Typography>
              <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-3">
                <Input label={t("teacherForm.pashto_slogan", "Pashto slogan")} value={form.sloganPs} onChange={(e) => updateField("sloganPs", e.target.value)} />
                <Input label={t("teacherForm.dari_slogan", "Dari slogan")} value={form.sloganPr} onChange={(e) => updateField("sloganPr", e.target.value)} />
                <Input label={t("teacherForm.english_slogan", "English slogan")} value={form.sloganEn} onChange={(e) => updateField("sloganEn", e.target.value)} />
              </div>
              <div className="mt-4 grid grid-cols-1 gap-4">
                <Textarea label={t("teacherForm.pashto_curriculum", "Pashto curriculum")} value={form.curriculumPs} onChange={(e) => updateField("curriculumPs", e.target.value)} />
                <Textarea label={t("teacherForm.dari_curriculum", "Dari curriculum")} value={form.curriculumPr} onChange={(e) => updateField("curriculumPr", e.target.value)} />
                <Textarea label={t("teacherForm.english_curriculum", "English curriculum")} value={form.curriculumEn} onChange={(e) => updateField("curriculumEn", e.target.value)} />
              </div>
            </div>
          </div>

          <div className="space-y-6">
            <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
              <Typography variant="h6" className="text-slate-900">
                {t("teacherForm.course_setup", "Course setup")}
              </Typography>
              <div className="mt-4 space-y-4">
                <div>
                  <label className="mb-2 block text-sm font-medium text-slate-700">{t("teacherForm.faculty", "Faculty")}</label>
                  <select
                    value={form.categoryId}
                    onChange={(e) => {
                      updateField("categoryId", e.target.value);
                      updateField("subCategoryId", "");
                    }}
                    className="w-full rounded-xl border border-slate-200 px-3 py-2"
                  >
                    <option value="">{t("teacherForm.select_faculty", "Select faculty")}</option>
                    {categories.map((category) => (
                      <option key={category._id} value={category._id}>
                        {category?.name?.[i18n.language] || category?.name?.en || t("teacherForm.unnamed_faculty", "Unnamed faculty")}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="mb-2 block text-sm font-medium text-slate-700">{t("teacherForm.department", "Department")}</label>
                  <select
                    value={form.subCategoryId}
                    onChange={(e) => updateField("subCategoryId", e.target.value)}
                    className="w-full rounded-xl border border-slate-200 px-3 py-2"
                  >
                    <option value="">{t("teacherForm.select_department", "Select department")}</option>
                    {subCategories.map((subCategory) => (
                      <option key={subCategory._id} value={subCategory._id}>
                        {subCategory?.[i18n.language] || subCategory?.en || t("teacherForm.unnamed_department", "Unnamed department")}
                      </option>
                    ))}
                  </select>
                </div>
                <Input label={t("teacherForm.duration", "Duration")} value={form.duration} onChange={(e) => updateField("duration", e.target.value)} />
                <div>
                  <label className="mb-2 block text-sm font-medium text-slate-700">{t("teacherForm.video_language", "Video language")}</label>
                  <select
                    value={form.videoLang}
                    onChange={(e) => updateField("videoLang", e.target.value)}
                    className="w-full rounded-xl border border-slate-200 px-3 py-2"
                  >
                    <option value="pashto">{t("pashto", "Pashto")}</option>
                    <option value="dari">{t("dari", "Dari")}</option>
                    <option value="english">{t("english", "English")}</option>
                  </select>
                </div>
                <div className="rounded-xl bg-slate-50 px-3 py-2 text-sm text-slate-600">{t("teacherForm.draft_only", "Courses are saved as drafts. An administrator publishes them when ready.")}</div>
                {courseSlug ? (
                  <div className="rounded-2xl bg-slate-50 px-4 py-3 text-sm text-slate-600">
                    {t("teacherForm.slug", "Slug")}: <span className="font-semibold text-slate-900">{courseSlug}</span>
                  </div>
                ) : null}
              </div>
            </div>

            {isEditMode ? (
              <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
                <Typography variant="h6" className="text-slate-900">
                  {t("teacherForm.thumbnail", "Thumbnail")}
                </Typography>
                <div className="mt-4 space-y-4">
                  {courseImage ? (
                    <img
                      src={`${ASSET_URI}/${courseImage}`}
                      alt={t("teacherForm.thumbnail_alt", "Course thumbnail")}
                      className="h-56 w-full rounded-2xl object-cover"
                      loading="lazy"
                      crossOrigin="anonymous"
                    />
                  ) : (
                    <div className="flex h-56 items-center justify-center rounded-2xl bg-slate-100 text-slate-500">
                      {t("teacherForm.no_thumbnail", "No thumbnail uploaded yet")}
                    </div>
                  )}

                  <input type="file" accept="image/*" onChange={handleImageUpload} />
                  {uploadingImage ? (
                    <div className="h-2 overflow-hidden rounded-full bg-slate-200">
                      <div className="h-full bg-indigo-600" style={{ width: `${imageProgress}%` }} />
                    </div>
                  ) : null}
                </div>
              </div>
            ) : null}
          </div>
        </div>

        <div className="flex flex-wrap justify-end gap-3 rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
          <Link to="/teacher/courses">
            <Button variant="outlined" color="blue-gray">
              {t("teacherForm.cancel", "Cancel")}
            </Button>
          </Link>
          <Button type="submit" color="indigo" disabled={saving || isLocked}>
            {saving
              ? t("teacherForm.saving", "Saving...")
              : isEditMode
                ? t("teacherForm.save_continue", "Save and Continue")
                : t("teacherForm.create_course", "Create Course")}
          </Button>
        </div>

        {isEditMode && canDelete ? (
          <div className="rounded-3xl border border-red-200 bg-red-50 p-6 shadow-sm">
            <Typography variant="h6" className="text-red-900">{t("teacherForm.danger_zone", "Danger zone")}</Typography>
            <Typography className="mt-2 text-sm text-red-800">
              {t("teacherForm.delete_description", "Remove this course from Teacher Studio. This action cannot be undone by you.")}
            </Typography>
            <div className="mt-4">
              <Button type="button" color="red" disabled={isLocked || deleting} onClick={() => setDeleteDialogOpen(true)}>
                {deleting ? t("teacherForm.deleting", "Removing...") : t("teacherForm.delete_course", "Delete")}
              </Button>
              {isLocked ? <p className="mt-2 text-sm text-amber-800">{t("teacherForm.delete_locked", "This course is locked by an administrator and cannot be deleted.")}</p> : null}
            </div>
          </div>
        ) : null}
      </form>

      {deleteDialogOpen ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/50 px-4" role="presentation">
          <div role="dialog" aria-modal="true" aria-labelledby="delete-course-title" className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
            <Typography id="delete-course-title" variant="h5" className="text-slate-900">
              {t("teacherForm.delete_confirmation_title", "Are you sure?")}
            </Typography>
            <Typography className="mt-3 text-slate-600">
              {t("teacherForm.delete_confirmation", "This course will be permanently removed from your list, and you cannot restore it.")}
            </Typography>
            <label className="mt-5 block text-sm font-medium text-slate-700" htmlFor="course-deletion-reason">
              {t("teacherForm.delete_reason", "Deletion reason")}
            </label>
            <Textarea
              id="course-deletion-reason"
              className="mt-2"
              value={deletionReason}
              onChange={(event) => setDeletionReason(event.target.value)}
              maxLength={500}
              disabled={deleting}
              label={t("teacherForm.delete_reason", "Deletion reason")}
            />
            <div className="mt-6 flex justify-end gap-3">
              <Button type="button" variant="outlined" color="blue-gray" disabled={deleting} onClick={() => { setDeleteDialogOpen(false); setDeletionReason(""); }}>
                {t("teacherForm.cancel", "Cancel")}
              </Button>
              <Button type="button" color="red" disabled={deleting || !deletionReason.trim()} onClick={handleDelete}>
                {deleting ? t("teacherForm.deleting", "Deleting...") : t("teacherForm.delete_course", "Delete")}
              </Button>
            </div>
          </div>
        </div>
      ) : null}
    </section>
  );
};

export default TeacherCourseForm;
